// Main view for daily execution and time-boxing.
import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { useAuth } from '@/features/auth';
import { useTasksContext, useTaskMutationsContext } from '@/features/tasks';
import {
  useBreakpoint,
  isBattlefieldBreakpoint,
} from '@/hooks/ui/useBreakpoint';
import { useT } from '@/i18n/useT';
import { DragOverlayContent } from '@/features/planning/components/shared/DragOverlayContent';
import { usePlanPageDnd } from '@/features/planning/hooks/use-plan-page-dnd.js';
import { usePlanMode } from '@/features/planning/hooks/use-plan-mode.js';
import useTaskStructure from '@/features/tasks/hooks/mutations/use-task-structure';
import { useGCal } from '@/features/planning/context/useGCal';
import { useTier } from '@/features/tier';
import { WallModal } from '@/features/wall';
import { APP_PAGE_FILL_SX } from '@/components/layout/appViewport';
import { PlanPageBattlefieldLayout } from '@/features/planning/pages/PlanPageBattlefieldLayout';
import { PlanPageDesktopLayout } from '@/features/planning/pages/PlanPageDesktopLayout';
import { PlanPageView } from '@/features/planning/pages/PlanPageView';
import { planPageOuterSx } from '@/features/planning/pages/planPageConstants';
import { buildPlanPageHeaderAndRail } from '@/features/planning/pages/buildPlanPageHeaderAndRail';
import { usePlanPageGCalWall } from '@/features/planning/hooks/use-plan-page-gcal-wall';
import { usePlanPageRailData } from '@/features/planning/hooks/use-plan-page-rail-data';
import { resolveGridView } from '@/features/planning/pages/planPageLayoutHelpers';
import OnboardingWelcomePlanTour from '@/features/onboarding/components/OnboardingWelcomePlanTour';
import { useOnboardingWelcomePlanTour } from '@/features/onboarding/hooks/useOnboardingWelcomePlanTour';
import { ONBOARDING_WELCOME_PLAN_TOUR_TEST_IDS } from '@/features/onboarding/constants/welcomePlanTour';
import { GCAL_ENABLED } from '@/config/featureFlags';

const PlanPage = () => {
  const t = useT();
  const currentBreakpoint = useBreakpoint();
  const isBattlefield = isBattlefieldBreakpoint(currentBreakpoint);
  const { user } = useAuth();
  const {
    allTasks,
    tasksMap,
    filteredTasks,
    projectFilter,
    error: tasksError,
  } = useTasksContext();
  const { updateTask, handleToggleComplete } = useTaskMutationsContext();
  const { moveTask } = useTaskStructure();
  const {
    isEnabled: isAgendaEnabled,
    toggleEnabled: toggleAgenda,
    syncStatus,
  } = useGCal();
  const { isFree } = useTier();
  const canUseAgenda = GCAL_ENABLED && !isFree;
  const { gcalWallOpen, closeGcalWall, handleToggleAgenda } =
    usePlanPageGCalWall({ canUseAgenda, toggleAgenda });
  const [planView, setPlanView] = useState(0);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [focusOwnerId, setFocusOwnerId] = useState(null);
  const activeView = isBattlefield ? 0 : planView;
  const { planMode, canUsePlanMode, onTogglePlanMode } = usePlanMode({
    projectFilter,
    activeView,
  });

  const {
    railSelectionHandlers,
    overdueTasks,
    unscheduledTasks,
    nextActions,
    plannedParentIds,
    waitingForParentIds,
    planRailTasks,
    overdueRailSlice,
    nextActionRailSlice,
    weekDays,
  } = usePlanPageRailData({
    filteredTasks,
    tasksMap,
    planMode,
    setFocusOwnerId,
    setSelectedTaskIds,
  });

  const welcomePlanTour = useOnboardingWelcomePlanTour({
    unscheduledTasks,
    nextActions,
    tasksMap,
  });

  const {
    sensors,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    collisionDetection,
    activeItem,
    isOverGrid,
    overGridWidth,
  } = usePlanPageDnd(
    allTasks,
    unscheduledTasks,
    tasksMap,
    user,
    updateTask,
    handleToggleComplete,
    activeView,
    planMode,
    planRailTasks,
    moveTask,
  );

  if (tasksError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t('planpage.accessDenied', 'Access denied.')}</Typography>
      </Box>
    );
  }

  const renderView = () => (
    <PlanPageView
      activeView={activeView}
      weekDays={weekDays}
      filteredTasks={filteredTasks}
      welcomePlanTour={welcomePlanTour}
    />
  );
  const { planHeader, railProps } = buildPlanPageHeaderAndRail({
    t,
    isBattlefield,
    activeView,
    setPlanView,
    planMode,
    canUsePlanMode,
    onTogglePlanMode,
    isAgendaEnabled,
    canUseAgenda,
    showAgendaToggle: GCAL_ENABLED,
    handleToggleAgenda,
    syncStatus,
    overdueTasks,
    nextActions,
    unscheduledTasks,
    overdueRailSlice,
    nextActionRailSlice,
    selectedTaskIds,
    railSelectionHandlers,
    focusOwnerId,
    renderView,
    planRailTasks,
    plannedParentIds,
    waitingForParentIds,
    tasksMap,
    welcomePlanTour,
  });
  const Layout = isBattlefield
    ? PlanPageBattlefieldLayout
    : PlanPageDesktopLayout;

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      sensors={sensors}
      collisionDetection={collisionDetection}
      autoScroll={false}
    >
      <Box
        sx={{
          ...planPageOuterSx(isBattlefield),
          ...APP_PAGE_FILL_SX,
          position: 'relative',
        }}
        data-testid={
          welcomePlanTour.active
            ? ONBOARDING_WELCOME_PLAN_TOUR_TEST_IDS.root
            : undefined
        }
      >
        {planHeader}
        {isBattlefield ? (
          <Layout {...railProps} />
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
            <Layout {...railProps} />
          </Box>
        )}
        <OnboardingWelcomePlanTour {...welcomePlanTour} />
      </Box>
      <DragOverlay dropAnimation={null} sx={{ opacity: 0.8 }}>
        <DragOverlayContent
          activeItem={activeItem}
          gridView={resolveGridView(isOverGrid, activeView)}
          overGridWidth={overGridWidth}
        />
      </DragOverlay>
      <WallModal
        open={gcalWallOpen}
        onClose={closeGcalWall}
        wallType="gcal_locked"
      />
    </DndContext>
  );
};

export default PlanPage;
